import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router';
import { LocaleProvider, useT } from '@/i18n/provider';
import { SITE_LOCALE, HREFLANG_TAGS, GUIDE_LOCALES } from '@/lib/site-config';
import { useInViewAnimation } from '@/hooks/useInViewAnimation';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { JsonLd, SiteJsonLd } from '@/components/JsonLd';
import { LandingHeader } from '@/components/LandingHeader';
import { HeroSection } from '@/components/HeroSection';
import { FeaturesSection } from '@/components/FeaturesSection';
import { MobileAppSection } from '@/components/MobileAppSection';
import { OutcomesSection } from '@/components/OutcomesSection';
import { HowItWorksSection } from '@/components/HowItWorksSection';
import { EngineerSection } from '@/components/EngineerSection';
import { PricingSection } from '@/components/PricingSection';
import { CTASection } from '@/components/CTASection';
import { SocialProofSection } from '@/components/SocialProofSection';
import { LandingFooter } from '@/components/LandingFooter';
// Homepage sections with heavy visuals (the ingest demo and the interactive
// investigator demo) load as separate chunks so the initial bundle stays lean
// — neither is needed for first paint. Each renders inside its own
// <Suspense fallback={null}> so the hero paints immediately; prerendering
// still captures the full page because the static server waits for
// networkidle0. The ingest demo sits directly under the hero now, so its
// chunk is the first thing wanted after paint: if a gap below the hero ever
// becomes visible on a slow connection, this is the import to make eager.
const IngestDemoSection = lazy(() => import('@/components/IngestDemoSection').then((m) => ({ default: m.IngestDemoSection })));
const AIAgentSection = lazy(() => import('@/components/AIAgentSection').then((m) => ({ default: m.AIAgentSection })));
import { ScrollToTop } from '@/components/ScrollToTop';
import { CloseTheLoopSection } from '@/components/CloseTheLoopSection';
import { WebMcpTools } from '@/components/WebMcpTools';
import { RumTracker } from '@/components/RumTracker';
import { LocaleSuggestion } from '@/components/LocaleSuggestion';
// Route page components are lazy-loaded so the homepage's initial bundle does
// not carry every marketing/docs/legal page. Each becomes its own chunk,
// fetched on navigation. Prerendering still captures them: the static server
// waits for the chunk to load and for <main>/<section> before snapshotting
// (verified, the same mechanism the always-lazy DocsLayout already relies on).
const TermsPage = lazy(() => import('@/pages/TermsPage').then((m) => ({ default: m.TermsPage })));
const PrivacyPage = lazy(() => import('@/pages/PrivacyPage').then((m) => ({ default: m.PrivacyPage })));
const SecurityPage = lazy(() => import('@/pages/SecurityPage').then((m) => ({ default: m.SecurityPage })));
const CookiePolicyPage = lazy(() => import('@/pages/CookiePolicyPage').then((m) => ({ default: m.CookiePolicyPage })));
const ImprintPage = lazy(() => import('@/pages/ImprintPage').then((m) => ({ default: m.ImprintPage })));
const ComparisonPage = lazy(() => import('@/pages/ComparisonPage').then((m) => ({ default: m.ComparisonPage })));
const PlanComparePage = lazy(() => import('@/pages/PlanComparePage').then((m) => ({ default: m.PlanComparePage })));
const CompareDuoPage = lazy(() => import('@/pages/CompareDuoPage').then((m) => ({ default: m.CompareDuoPage })));
const BlogPage = lazy(() => import('@/pages/BlogPage').then((m) => ({ default: m.BlogPage })));
const BlogPostPage = lazy(() => import('@/pages/BlogPostPage').then((m) => ({ default: m.BlogPostPage })));
const ChangelogPage = lazy(() => import('@/pages/ChangelogPage').then((m) => ({ default: m.ChangelogPage })));
const ContactPage = lazy(() => import('@/pages/ContactPage').then((m) => ({ default: m.ContactPage })));
const CommunityPage = lazy(() => import('@/pages/CommunityPage').then((m) => ({ default: m.CommunityPage })));
const AISearchPage = lazy(() => import('@/pages/AISearchPage').then((m) => ({ default: m.AISearchPage })));
const McpPage = lazy(() => import('@/pages/McpPage').then((m) => ({ default: m.McpPage })));
const CloseTheLoopPage = lazy(() => import('@/pages/CloseTheLoopPage').then((m) => ({ default: m.CloseTheLoopPage })));
const FleetPage = lazy(() => import('@/pages/FleetPage').then((m) => ({ default: m.FleetPage })));
const SiemPage = lazy(() => import('@/pages/SiemPage').then((m) => ({ default: m.SiemPage })));
const ServiceIntelligenceMarketingPage = lazy(() => import('@/pages/ServiceIntelligenceMarketingPage').then((m) => ({ default: m.ServiceIntelligenceMarketingPage })));
const AgenticSocPage = lazy(() => import('@/pages/AgenticSocPage').then((m) => ({ default: m.AgenticSocPage })));
const Nis2Page = lazy(() => import('@/pages/Nis2Page').then((m) => ({ default: m.Nis2Page })));
const DoraPage = lazy(() => import('@/pages/DoraPage').then((m) => ({ default: m.DoraPage })));
const ClickhousePage = lazy(() => import('@/pages/ClickhousePage').then((m) => ({ default: m.ClickhousePage })));
const ResellerPage = lazy(() => import('@/pages/ResellerPage').then((m) => ({ default: m.ResellerPage })));
const PricingPage = lazy(() => import('@/pages/PricingPage').then((m) => ({ default: m.PricingPage })));
const AboutPage = lazy(() => import('@/pages/AboutPage').then((m) => ({ default: m.AboutPage })));
const TopdeskIntegrationPage = lazy(() => import('@/pages/TopdeskIntegrationPage').then((m) => ({ default: m.TopdeskIntegrationPage })));
const ServicenowIntegrationPage = lazy(() => import('@/pages/ServicenowIntegrationPage').then((m) => ({ default: m.ServicenowIntegrationPage })));
const GlossaryPage = lazy(() => import('@/pages/GlossaryPage').then((m) => ({ default: m.GlossaryPage })));
const GuidesPage = lazy(() => import('@/pages/GuidesPage').then((m) => ({ default: m.GuidesPage })));
const GuidePage = lazy(() => import('@/pages/GuidePage').then((m) => ({ default: m.GuidePage })));
const ArchitecturePage = lazy(() => import('@/pages/ArchitecturePage').then((m) => ({ default: m.ArchitecturePage })));
const StatusPage = lazy(() => import('@/pages/StatusPage').then((m) => ({ default: m.StatusPage })));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage })));
const DocsLayout = lazy(() => import('@/pages/docs/DocsLayout'));
const AlternativesHubPage = lazy(() => import('@/pages/AlternativesHubPage').then((m) => ({ default: m.AlternativesHubPage })));
const AlternativePage = lazy(() => import('@/pages/AlternativePage').then((m) => ({ default: m.AlternativePage })));

function Landing() {
  useInViewAnimation();
  const t = useT();

  useDocumentMeta({
    title: t('meta.home.title'),
    description: t('meta.home.description'),
  });

  // FAQ JSON-LD targets natural-language search queries like
  // "logs doorzoeken met AI", "search logs with AI", "Logs durchsuchen mit KI",
  // "rechercher des logs avec l'IA". One Q&A per language matches each locale's
  // primary head-keyword.
  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    inLanguage: HREFLANG_TAGS[SITE_LOCALE],
    mainEntity: [1, 2, 3, 4, 5].map((i) => ({
      '@type': 'Question',
      name: t(`meta.faq.q${i}`),
      acceptedAnswer: {
        '@type': 'Answer',
        text: t(`meta.faq.a${i}`),
      },
    })),
  };

  return (
    <div className="min-h-screen">
      <JsonLd data={faqJsonLd} />
      <LandingHeader />
      {/* The page reads as one argument, top to bottom: a moment the reader
          recognises (hero), then what the thing actually is, shown rather than
          claimed (the demo), and only then what it is worth — a value claim is
          indistinguishable from a competitor's until the reader knows what the
          product is. Setup effort and the promises we make sit low, next to the
          price, where the doubt they answer actually arises. Close the loop
          speaks to a different buyer (a developer with a coding agent) and sits
          with the technical layer rather than in the reader's third screen. */}
      <main>
        <HeroSection />
        <Suspense fallback={null}><IngestDemoSection /></Suspense>
        <OutcomesSection />
        <Suspense fallback={null}><AIAgentSection /></Suspense>
        <FeaturesSection />
        <MobileAppSection />
        <HowItWorksSection />
        <SocialProofSection />
        <PricingSection />
        <EngineerSection />
        <CloseTheLoopSection />
        <CTASection />
      </main>
      <LandingFooter />
    </div>
  );
}

function DocsLoadingFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-950">
      <div className="animate-spin h-8 w-8 border-4 border-primary-500 border-t-transparent rounded-full" />
    </div>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <LocaleProvider>
        <SiteJsonLd />
        <ScrollToTop />
        <WebMcpTools />
        <RumTracker />
        <LocaleSuggestion />
        <Suspense fallback={<DocsLoadingFallback />}>
          <Routes>
          {/* Marketing: served on every locale domain at root paths */}
          <Route path="/" element={<Landing />} />
          <Route path="/ai-log-search" element={<AISearchPage />} />
          <Route path="/mcp" element={<McpPage />} />
          <Route path="/close-the-loop" element={<CloseTheLoopPage />} />
          <Route path="/agents" element={<FleetPage />} />
          <Route path="/siem" element={<SiemPage />} />
          <Route path="/service-intelligence" element={<ServiceIntelligenceMarketingPage />} />
          <Route path="/agentic-soc" element={<AgenticSocPage />} />
          <Route path="/clickhouse-log-management" element={<ClickhousePage />} />
          <Route path="/reseller" element={<ResellerPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/integrations/topdesk" element={<TopdeskIntegrationPage />} />
          <Route path="/integrations/servicenow" element={<ServicenowIntegrationPage />} />
          <Route path="/compliance/nis2" element={<Nis2Page />} />
          <Route path="/compliance/dora" element={<DoraPage />} />
          <Route path="/glossary" element={<GlossaryPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/cookies" element={<CookiePolicyPage />} />
          <Route path="/imprint" element={<ImprintPage />} />
          <Route path="/security" element={<SecurityPage />} />
          <Route path="/architecture" element={<ArchitecturePage />} />
          <Route path="/status" element={<StatusPage />} />
          <Route path="/compare" element={<ComparisonPage />} />
          <Route path="/pricing/compare" element={<PlanComparePage />} />
          <Route path="/compare/:slug" element={<CompareDuoPage />} />
          <Route path="/changelog" element={<ChangelogPage />} />
          <Route path="/contact" element={<ContactPage />} />

          {/* English-only sections: only mounted on the .io build.
              On ccTLDs these paths 301 to logpulse.io via _redirects. */}
          {SITE_LOCALE === 'en' && (
            <>
              <Route path="/community" element={<CommunityPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="/alternatives" element={<AlternativesHubPage />} />
              <Route path="/alternatives/:slug" element={<AlternativePage />} />
              <Route path="/docs/*" element={<DocsLayout />} />
            </>
          )}

          {/* Guides are locale-aware: mounted on every domain that has guides
              in its language (GUIDE_LOCALES). English-only guide slugs on a
              ccTLD 301 to logpulse.io via _redirects. */}
          {GUIDE_LOCALES.includes(SITE_LOCALE) && (
            <>
              <Route path="/guides" element={<GuidesPage />} />
              <Route path="/guides/:slug" element={<GuidePage />} />
            </>
          )}

          <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </LocaleProvider>
    </BrowserRouter>
  );
}
